import React, { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  DateTimePickerDropdown,
  HSpacer,
  Input,
  Select,
  Text,
  TextAreaInput,
  VSpacer,
} from '@/components/DesignSystem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IconButton, Menu, MenuItem, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InsertInvitation from '@mui/icons-material/InsertInvitation';
import CheckIcon from '@mui/icons-material/Check';
import { ScheduledNotificationApi } from '@/utilities/api/ScheduledNotificationApi';
import { useQuery } from 'react-query';
import { QueryKeys } from '@/constants/QueryKeys';
import {
  DeepLinkUrls,
  FrenchSuffix,
  InsertVariables,
} from '@shared/enums/ScheduledNotificationType';
import { PreviewNotification } from './PreviewNotification';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { DateTime } from 'luxon';
import { downloadCsvErrors, handleEditSection, isFutureDate } from '../utils/notificationFormUtils';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { NotificationRecipientsModal } from './NotificationRecipientsModal';
import { CustomerFilterModal } from './CustomerFilterModal';
import { BaseNotificationProps } from '../utils/NotificationTypes';
import {
  CsvError,
  FilterOptions,
  CSVRecipientData,
} from '@shared/interfaces/ScheduledNotification';
import { CsvUploadModal } from './CsvUploadModal';
import { formatFilterDisplay } from '../utils/filterFormatting';
import { ScheduledNotificationEndpoint } from '@api/endpoints/ScheduledNotificationEndpoint';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDeepLinkUrlLabel } from '../utils/useDeepLinkUrlLabel';
import { useFilterDisplayContext } from '../utils/useFilterDisplayContext';
import { Country } from "@shared/enums";

interface NotificationCustomizationContentProps extends BaseNotificationProps {
  initialOptions?: FilterOptions | null,
  initialRecipientData?: CSVRecipientData,
  deepLink: DeepLinkUrls | null,
  message: string,
  setDeepLink: (link: DeepLinkUrls) => void,
  setMessage: (text: string) => void,
  setOptions?: (options: FilterOptions) => void,
  setRecipientCount?: (count: number | null) => void,
  setRecipientIds?: (ids: CSVRecipientData) => void,
  setSendDate: (date: Date | undefined) => void,
  setShowConfirmationDialog: (show: boolean) => void,
  setTitle: (title: string) => void,
  testID: string,
  title: string,
}

export const NotificationAddForm = ({
  deepLink,
  initialOptions = {},
  initialRecipientData = [],
  message,
  notificationId,
  recipientCount: initialRecipientCount,
  retailerId,
  sendDate,
  setDeepLink,
  setMessage,
  setOptions: setParentOptions,
  setRecipientCount: setParentRecipientCount,
  setRecipientIds: setParentRecipientIds,
  setSendDate,
  setShowConfirmationDialog,
  setTitle,
  status = null,
  testID,
  title,
} : NotificationCustomizationContentProps) => {
  const { t, i18n } = useTranslation();
  const deepLinkLabel = useDeepLinkUrlLabel();
  const [recipientMenuAnchorEl, setRecipientMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [dateTimePickerAnchorEl, setDateTimePickerAnchorEl] = useState<null | HTMLElement>(null);
  const [variableMenuAnchorEl, setVariableMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState<boolean>(false);
  const [options, setOptions] = useState<FilterOptions>(initialOptions || {});
  const [csvRecipientData, setCsvRecipientData] = useState<CSVRecipientData>(
    initialRecipientData ?? [],
  );
  const [isEditMode, setIsEditMode] = useState<boolean>(!!notificationId);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(
    (initialOptions ? Object.keys(initialOptions).length === 0 : true) &&
    !!initialRecipientCount &&
    (Array.isArray(initialRecipientData) ? initialRecipientData.length === 0 : true),
  );
  const [recipientCount, setRecipientCount] = useState<number | null>(
    typeof initialRecipientCount === 'number' ? initialRecipientCount : null,
  );
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] =
    useState<ScheduledNotificationEndpoint.ValidateInsertVariables.Response['errors']>([]);
  const [csvErrors, setCsvErrors] = useState<CsvError[] | null>(null);
  const [csvWarnings, setCsvWarnings] = useState<CsvError[] | null>(null);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);
  const [isUploadingCsv, setIsUploadingCsv] = useState<boolean>(false);
  const [validationBanner, setValidationBanner] = useState<{
    type: 'error' | 'warning';
    message: string;
  } | null>(null);

  const { openSnackbar } = useSnackbar();
  const allRecipient = 'all';

  const { filterDisplayContext, retailer, subwallets } = useFilterDisplayContext(retailerId);

  const onCSVUpload = async (file: File | null) => {
    if (!file) return;
    setIsUploadingCsv(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('retailerId', retailerId ?? '');

      const data = await ScheduledNotificationApi.getRecipientsFromCsv(formData);
      setOptions({});
      setIsAllSelected(false);

      if (data.count > 0) {
        setCsvRecipientData(data.csvRecipientData);
        setRecipientCount(data.count);
        setParentRecipientCount && setParentRecipientCount(data.count);
        setRecipientsError(null);

      } else {
        setCsvRecipientData([]);
        setRecipientCount(null);
        setParentRecipientCount && setParentRecipientCount(null);
      }

      const hasErrors = data.errors && data.errors.length > 0;
      const hasWarnings = data.warnings && data.warnings.length > 0;
      setCsvErrors(hasErrors ? data.errors as unknown as CsvError[] : null);
      setCsvWarnings(hasWarnings ? data.warnings as unknown as CsvError[] : null);

      if (hasErrors) {
        const csvUploadedMsg = t(
          $ => $.notificationBuilder.form.success.csvUploadedWithErrors,
          { successCount: data.count, errorCount: data.errors.length },
        );
        openSnackbar(csvUploadedMsg, 6000, false);
      } else if (hasWarnings) {
        const csvUploadedMsg = t(
          $ => $.notificationBuilder.form.success.csvUploadedWithWarnings,
          { successCount: data.count, warningCount: data.warnings.length },
        );
        openSnackbar(csvUploadedMsg, 6000, false);
      } else {
        openSnackbar(t($ => $.notificationBuilder.form.success.csvUploaded), 4000, false);
      }
    } catch (err) {
      const errorMsg = err instanceof Error
        ? err.message
        : t($ => $.notificationBuilder.form.errors.csvUploadFailed);
      openSnackbar(errorMsg, 5000, true);
    } finally {
      setIsUploadingCsv(false);
      setShowCsvUploadModal(false);
    }
  };

  const fetchRecipientCount = async () => {
    if (!retailerId) {
      return null;
    }
    try {
      const response = await ScheduledNotificationApi.getRecipientsCount({
        retailerId,
        options: options as FilterOptions,
        csvRecipientData: csvRecipientData,
      });
      return isNil(response.count) ? null : response.count;

    } catch (error) {
      openSnackbar(t($ => $.notificationBuilder.form.errors.recipientCountFailed));
    }
  };

  const {
    data: fetchedRecipientCount,
    isFetching: isFetchingRecipientCount,
    error: recipientCountError,
  } = useQuery(
    [QueryKeys.FETCH_RECIPIENT_COUNT, retailerId, options, isAllSelected],
    fetchRecipientCount,
    {
      enabled: (!isEditMode && (Object.keys(options).length > 0 || isAllSelected)),
      initialData: initialRecipientCount,
      staleTime: 0,
      cacheTime: 0,
    },
  );

  useEffect(() => {
    if (setParentOptions) {
      setParentOptions(options);
    }
  }, [options, setParentOptions]);

  useEffect(() => {
    if (setParentRecipientIds) setParentRecipientIds(csvRecipientData);
  }, [csvRecipientData, setParentRecipientIds]);

  useEffect(() => {
    if (!isNil(fetchedRecipientCount)) {
      setRecipientCount(fetchedRecipientCount);
      setParentRecipientCount && setParentRecipientCount(fetchedRecipientCount);

      if (typeof fetchedRecipientCount === 'number' && fetchedRecipientCount > 0) {
        setRecipientsError(null);
      } else {
        setRecipientsError(t($ => $.notificationBuilder.form.errors.noMatchingRecipients));
      }
    }
  }, [fetchedRecipientCount, setParentRecipientCount, t]);

  useEffect(() => {
    if (recipientCountError) {
      openSnackbar(t($ => $.notificationBuilder.form.errors.recipientCountFailed), 5000, true);
    }
  }, [recipientCountError, openSnackbar, t]);

  const handleRecipientAction = (actionOptions: typeof allRecipient | FilterOptions) => {
    setIsEditMode(false);
    setCsvRecipientData([]);
    setCsvErrors(null);
    setCsvWarnings(null);
    setRecipientsError(null);
    if (actionOptions === allRecipient) {
      setOptions({});
      setIsAllSelected(true);
    } else {
      if (Object.keys(actionOptions).length === 0) {
        setOptions({});
        setIsAllSelected(true);
      } else {
        setOptions(actionOptions);
        setIsAllSelected(false);
      }
    }
  };

  const handleVariableInsert = (variableId: string, variableLabel: string) => {
    const variableText = `{{${variableLabel}}}`;
    const trimmedMessage = message.trimEnd();
    const newText = trimmedMessage ? `${trimmedMessage} ${variableText}` : variableText;
    setMessage(newText);
    setVariableMenuAnchorEl(null);
    setValidationBanner(null); // Clear any existing validation banner when inserting new variable
  };

  const shouldDisableVariable = (variableValue: InsertVariables): boolean => {
    if (
      variableValue === InsertVariables.PointsExpiringOnSpecifiedDate ||
      variableValue === InsertVariables.PointsExpiringOnSpecifiedDateFrench
    ) {
      return !options.pointsExpiringOnDate;
    }

    const selectedIds = options.subwalletIds ?? [];

    if (
      variableValue === InsertVariables.RetailerPoints ||
      variableValue === InsertVariables.RetailerPointsFrench
    ) {
      const hasRetailerSelection = subwallets?.some(
        sw => sw.manufacturerId === null && selectedIds.includes(sw.id),
      );
      return !hasRetailerSelection;
    }

    if (
      variableValue === InsertVariables.ManufacturerPoints ||
      variableValue === InsertVariables.ManufacturerPointsFrench
    ) {
      const hasManufacturerSelection = subwallets?.some(
        sw => sw.manufacturerId !== null && selectedIds.includes(sw.id),
      );
      return !hasManufacturerSelection;
    }

    return false;
  };

  const validateMessageText = useCallback(
    (message: string): { type: 'error'; message: string } | null => {
      if (!message.trim()) {
        return null;
      }

      const stopPattern = /reply\s+stop/i;
      if (stopPattern.test(message)) {
        return {
          type: 'error',
          message: t($ => $.notificationBuilder.form.content.validation.noStopText),
        };
      }

      const openBrackets = (message.match(/\{\{/g) || []).length;
      const closeBrackets = (message.match(/\}\}/g) || []).length;

      if (openBrackets !== closeBrackets) {
        return {
          type: 'error',
          message: t($ => $.notificationBuilder.form.content.validation.mismatchedBrackets),
        };
      }

      // Matches {{variable}} with optional surrounding whitespace. Outer \{\{ \}\} are the literal
      // delimiters; \s* allows spaces inside them; ([^}]+?) captures the variable name lazily
      // so nested braces don't bleed into adjacent variables.
      const variablePattern = /\{\{\s*([^}]+?)\s*\}\}/g;
      const matches = [...message.matchAll(variablePattern)];
      const enumByLower = new Map(
        Object.values(InsertVariables).map(v => [v.toLowerCase(), v]),
      );

      for (const match of matches) {
        const variableText = match[1].trim().toLowerCase();
        const enumValue = enumByLower.get(variableText);

        if (!enumValue) {
          return {
            type: 'error',
            message: t(
              $ => $.notificationBuilder.form.validation.invalidVariable,
              { variable: match[1] },
            ),
          };
        }

        if (shouldDisableVariable(enumValue)) {
          return {
            type: 'error',
            message: t(
              $ => $.notificationBuilder.form.validation.disabledVariable,
              { variable: match[1] },
            ),
          };
        }
      }

      return null;
    },
    [t, options, subwallets],
  );
  useEffect(() => {
    if (message.trim()) {
      setValidationBanner(validateMessageText(message));
    } else {
      setValidationBanner(null);
    }
  }, [
    message,
    isAllSelected,
    options,
    csvRecipientData,
    sendDate,
    recipientCount,
    validateMessageText,
  ]);

  if (showPreview) {
    return (
      <PreviewNotification
        csvRecipientData={csvRecipientData}
        deepLink={deepLink}
        message={message}
        onCreateNotification={() => setShowConfirmationDialog(true)}
        onEditSection={(section) => handleEditSection(section, testID, setShowPreview)}
        options={options}
        recipientCount={recipientCount}
        retailerId={retailerId}
        sendDate={sendDate}
        status={status}
        testID={`${testID}-preview`}
        title={title}
      />
    );
  }

  function extractSelectedVariablesFromMessage (message: string): InsertVariables[] {
    if (!message) return [];
    const lower = message.toLowerCase();
    const hasToken = (label: string) => lower.includes(`{{${label.toLowerCase()}}}`);
    return (Object.values(InsertVariables) as string[]).filter(hasToken) as InsertVariables[];
  }

  const handleContinue = async () => {
    setValidationErrors([]);
    setValidationBanner(null);
    setValidating(true);

    try {
      // First validate message text (brackets and variables)
      const messageValidation = validateMessageText(message);
      if (messageValidation) {
        setValidationBanner(messageValidation);
        setValidating(false);
        return;
      }

      const selectedVariables = extractSelectedVariablesFromMessage(message);
      const res = await ScheduledNotificationApi.validateInsertVariables({
        retailerId,
        csvRecipientData: csvRecipientData,
        options: options as FilterOptions,
        selectedVariables,
      });

      if (res.canFinalize) {
        setShowPreview(true);
      } else {
        setValidationErrors(res.errors);
      }
    } catch (e) {
      openSnackbar(
        t($ => $.notificationBuilder.form.content.validation.variableValidationFailed),
        5000,
        true,
      );
    } finally {
      setValidating(false);
    }
  };

  return (
    <Stack sx={{ width: '720px', margin: '0 auto' }}>
      <Card
        id={`${testID}-schedule-section`}
        sx={{
          padding: '16px 24px',
          '& .MuiCardContent-root': {
            padding: '0px',
            '&:last-child': {
              paddingBottom: '0px',
            },
          },
        }}
        testID={`${testID}-schedule-card`}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Text
            category="title-medium"
            sx={{ fontSize: '18px' }}
          >
            {t($ => $.notificationBuilder.form.schedule.title)}
          </Text>
          {!sendDate ? (
            <Button
              color="primary"
              onClick={(event) => {
                setDateTimePickerAnchorEl(event.currentTarget);
              }}
              startIcon={<InsertInvitation />}
              testID={`${testID}-choose-date-button`}
            >
              {t($ => $.notificationBuilder.form.schedule.chooseDateTime)}
            </Button>
          ) : (
            <Stack
              alignItems="center"
              direction="row"
              sx={{ cursor: 'pointer' }}
            >
              <Text category="body-large">
                {t($ => $.common.dateAtTime, {
                  date: DateTime.fromJSDate(sendDate)
                    .setLocale(i18n.language)
                    .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
                  time: DateTime.fromJSDate(sendDate)
                    .setLocale(i18n.language)
                    .toLocaleString(DateTime.TIME_SIMPLE),
                })}
              </Text>
              <IconButton
                aria-label={t($ => $.notificationBuilder.form.recipients.editDate)}
                color='primary'
                data-testid={`${testID}-edit-date-button`}
                onClick={(event) => {
                  setDateTimePickerAnchorEl(event.currentTarget);
                }}
              >
                <EditIcon fontSize="medium" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Card>

      <VSpacer size="6" />

      <Card
        id={`${testID}-recipient-section`}
        sx={{
          padding: '16px 24px',
          '& .MuiCardContent-root': {
            padding: '0px',
            '&:last-child': {
              paddingBottom: '0px',
            },
          },
        }}
        testID={`${testID}-recipients-card`}
      >
        <Stack
          alignItems="flex-start"
          direction="column"
          sx={{
            width: '100%',
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            sx={{
              width: '100%',
              display: 'flex',
            }}
          >
            <Text
              category="title-medium"
              sx={{ fontSize: '18px' }}
            >
              {(() => {
                if (isFetchingRecipientCount || isUploadingCsv) {
                  return t($ => $.notificationBuilder.form.loadingRecipients);
                }

                if (recipientCount !== null) {
                  return t(
                    $ => $.notificationBuilder.form.recipients.title,
                    { count: recipientCount },
                  );
                }

                const hasNoRecipients = Object.keys(options).length === 0 &&
                  !isAllSelected &&
                  csvRecipientData.length === 0;

                return hasNoRecipients && t($ => $.notificationBuilder.form.recipients.addTitle);
              })()}
            </Text>
            {(
              Object.keys(options).length > 0 ||
              isAllSelected ||
              csvRecipientData.length > 0
            ) ? (
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  {((!!recipientCount && recipientCount > 0) || isFetchingRecipientCount) && (
                    <Button
                      color="primary"
                      disabled={isFetchingRecipientCount}
                      onClick={() => setShowRecipientsModal(true)}
                      testID={`${testID}-view-recipients-button`}
                      variant="text"
                    >
                      {isFetchingRecipientCount
                        ? t($ => $.common.loading)
                        : t($ => $.notificationBuilder.actions.viewRecipients)}
                    </Button>
                  )}
                  <IconButton
                    aria-label={t($ => $.notificationBuilder.actions.editRecipients)}
                    color='primary'
                    data-testid={`${testID}-edit-recipients-button`}
                    onClick={(event) => setRecipientMenuAnchorEl(event.currentTarget)}
                  >
                    <EditIcon fontSize="medium" />
                  </IconButton>
                </Stack>
              ) : (
                <Button
                  color="primary"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(event) => setRecipientMenuAnchorEl(event.currentTarget)}
                  testID={`${testID}-select-recipients-button`}
                >
                  {t($ => $.notificationBuilder.form.recipients.select)}
                </Button>
              )}
          </Stack>

          {csvErrors && csvErrors.length > 0 && (
            <Button
              color="error"
              onClick={() => downloadCsvErrors(csvErrors)}
              size="small"
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                padding: '2px 4px',
                minWidth: 'auto',
                textDecoration: 'underline',
                marginTop: '4px',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              }}
              testID={`${testID}-csv-error-download`}
              variant="text"
            >
              {t($ => $.notificationBuilder.form.recipients.failedWithErrors, {
                count: csvErrors.length,
              })}
            </Button>
          )}

          {csvWarnings && csvWarnings.length > 0 && (
            <Button
              color="warning"
              onClick={() => downloadCsvErrors(csvWarnings, 'upload-warnings')}
              size="small"
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                padding: '2px 4px',
                minWidth: 'auto',
                textDecoration: 'underline',
                marginTop: '4px',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              }}
              testID={`${testID}-csv-warning-download`}
              variant="text"
            >
              {t($ => $.notificationBuilder.form.recipients.successWithWarnings, {
                count: csvWarnings.length,
              })}
            </Button>
          )}

          {(
            Object.keys(options).length > 0 ||
            isAllSelected ||
            csvRecipientData.length > 0
          ) && (
            <Stack
              direction="column"
              spacing={1}
              sx={{
                mt: 1,
                width: '100%',
              }}
            >
              {isAllSelected ? (
                <Stack alignItems="center" direction="row" spacing={1}>
                  <CheckIcon color="success" fontSize="small" />
                  <Text category="body-medium">
                    {t($ => $.notificationBuilder.form.recipients.allCustomersSelected)}
                  </Text>
                </Stack>
              ) : csvRecipientData.length > 0 ? (
                <Stack alignItems="center" direction="row" spacing={1}>
                  <CheckIcon color="success" fontSize="small" />
                  <Text category="body-medium">
                    {t(
                      $ => $.notificationBuilder.form.recipients.csvCustomersSelected,
                      { count: recipientCount ?? 0 },
                    )}
                  </Text>
                </Stack>
              ) : (
                Object.entries(options).map(([key, value]) => {
                  if ((Array.isArray(value) && value.length === 0) || value === null) {
                    return null;
                  }
                  const formattedText = formatFilterDisplay(
                    key as keyof FilterOptions, value, options, filterDisplayContext,
                  );
                  return formattedText ? (
                    <Stack
                      alignItems="center"
                      direction="row"
                      key={key}
                      spacing={1}
                    >
                      <CheckIcon color="success" fontSize="small" />
                      <Text category="body-medium">
                        {formattedText}
                      </Text>
                    </Stack>
                  ) : null;
                })
              )}
            </Stack>

          )}

          {recipientsError && (
            <>
              <VSpacer size="4" />
              <Stack alignItems="center" direction="row" spacing={1}>
                <Text
                  category="body-medium"
                  sx={{ color: 'error.main' }}
                >
                  {recipientsError}
                </Text>
              </Stack>
            </>
          )}

          <Menu
            anchorEl={recipientMenuAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            onClose={() => setRecipientMenuAnchorEl(null)}
            open={Boolean(recipientMenuAnchorEl)}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: '8px',
                width: '220px',
              },
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => {
              setShowFilterModal(true);
              setRecipientMenuAnchorEl(null);
            }}>
              {t($ => $.notificationBuilder.form.recipients.menu.filterCustomers)}
            </MenuItem>
            <MenuItem onClick={() => {
              setShowCsvUploadModal(true);
              setRecipientMenuAnchorEl(null);
            }}>
              {t($ => $.notificationBuilder.form.recipients.menu.uploadCsv)}
            </MenuItem>
            <MenuItem onClick={() => {
              handleRecipientAction(allRecipient);
              setRecipientMenuAnchorEl(null);
            }}>
              {t($ => $.notificationBuilder.form.recipients.menu.addAll)}
            </MenuItem>
          </Menu>
        </Stack>
      </Card>

      <VSpacer size="6" />

      <Card
        id={`${testID}-content-card`}
        sx={{ padding: '24px' }}
        testID={`${testID}-content-card`}
      >
        <Text
          category="title-medium"
          sx={{
            fontSize: '18px',
          }}
        >
          {t($ => $.notificationBuilder.form.content.title)}
        </Text>

        <VSpacer size="6" />
        <Text category="body-medium" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {t($ => $.notificationBuilder.form.content.notificationTitle)}
        </Text>
        <VSpacer size="2" />
        <Input
          maxLength={100}
          onChangeText={setTitle}
          placeholder={t($ => $.notificationBuilder.form.content.titlePlaceholder)}
          showCharacterLimit='countdown'
          sx={{ width: '100%' }}
          testID={`${testID}-notification-title`}
          value={title}
        />
        <VSpacer size="6" />
        <Text category="body-medium" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {t($ => $.notificationBuilder.form.content.messageText)}
        </Text>
        <VSpacer size="2" />
        <div style={{ position: 'relative' }}>
          <TextAreaInput
            InputProps={{
              sx: {
                '& .MuiInputBase-input': {
                  padding: '2px',
                },
              },
            }}
            id={`${testID}-message-textarea`}
            maxLength={1200}
            onChangeText={setMessage}
            showCharacterLimit='countdown'
            testID={`${testID}-message-text`}
            value={message}
          />
          <VSpacer size="3" />
          <Button
            onClick={(event) => setVariableMenuAnchorEl(event.currentTarget)}
            size="medium"
            sx={{
              position: 'absolute',
              bottom: '0px',
              transform: 'translateY(100%)',
              left: '0px',
              boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.15)',
              borderRadius: '0',
              padding: '8px 12px',
              color: '#141414',
            }}
            testID={`${testID}-insert-variable`}
            variant="text"
          >
            {t($ => $.notificationBuilder.form.content.insertVariable)}
            <HSpacer size="2" />
            <DataObjectIcon fontSize="small" />
          </Button>
          <Menu
            anchorEl={variableMenuAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onClose={() => setVariableMenuAnchorEl(null)}
            open={Boolean(variableMenuAnchorEl)}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: '8px',
              },
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {Object.entries(InsertVariables)
              .filter(([, value]) => {
                return !(retailer?.country === Country.UnitedStates
                  && value.endsWith(FrenchSuffix));
              })
              .map(([key, value]) => {
                const disabled = shouldDisableVariable(value);
                return (
                <MenuItem
                  disabled={disabled}
                  key={key.toLowerCase()}
                  onClick={() => !disabled && handleVariableInsert(key.toLowerCase(), value)}
                  sx={{
                    opacity: disabled ? 0.5 : 1,
                    pointerEvents: disabled ? 'none' : 'auto',
                  }}
                >
                  {value}
                </MenuItem>
                );
              })}
          </Menu>
        </div>
        <VSpacer size="9" />
        {!!validationErrors.length && (
          <div style={{ marginTop: 12 }}>
            {validationErrors.map((err, i) => (
              <Text key={i} sx={{ color: 'error.main' }}>
                • {err.message}
              </Text>
            ))}
          </div>
        )}
        <VSpacer size="6" />
        <Text category="body-medium" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {t($ => $.notificationBuilder.form.content.deepLink)}
        </Text>
        <VSpacer size="2" />
        <Select
          fullWidth
          label={t($ => $.notificationBuilder.form.content.selectDeepLink)}
          onChangeValue={(value) => setDeepLink(value as DeepLinkUrls)}
          testID={`${testID}-deep-link`}
          value={deepLink}
        >
          {Object.entries(DeepLinkUrls).map(([key, value]) => (
            <MenuItem
              key={key}
              value={value}
            >
              {deepLinkLabel[value]}
            </MenuItem>
          ))}
        </Select>
        <VSpacer size="6" />
        <Text category="body-medium">
          {t($ => $.notificationBuilder.form.content.autoUnsubscribeText)}
        </Text>
      </Card>

      <VSpacer size="6" />

      <VSpacer size="7" />

      {validationBanner && (
        <>
          <Banner
            status={validationBanner.type}
            sx={{
              marginBottom: '16px',
              '& .MuiAlert-action': {
                display: 'none',
              },
            }}
            title={validationBanner.message}
          />
        </>
      )}

      <Stack alignItems="center" justifyContent="center">
        <Button
          color="primary"
          disabled={
            validating ||
            !message.trim() ||
            !sendDate ||
            !recipientCount ||
            isFetchingRecipientCount ||
            (validationBanner?.type === 'error')
          }
          onClick={handleContinue}
          size="large"
          testID={`${testID}-create-button`}
          variant="contained"
        >
          {t($ => $.common.actions.continue)}
        </Button>
      </Stack>

      <div style={{
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        height: 0,
        overflow: 'hidden',
      }}>
        <DateTimePickerDropdown
          anchorEl={dateTimePickerAnchorEl}
          disabled={false}
          label={t($ => $.notificationBuilder.form.schedule.label)}
          onChange={(date?: Date) => {
            if (date) {
              if (isFutureDate(date)) {
                setSendDate(date);
                setDateTimePickerAnchorEl(null);
              } else {
                openSnackbar(
                  t($ => $.notificationBuilder.modal.detail.validation.futureDateTime),
                  5000,
                  true,
                );
              }
            }
          }}
          onClose={() => setDateTimePickerAnchorEl(null)}
          testID={`${testID}-datetime-picker`}
          value={sendDate}
          width={350}
        />
      </div>

      {showRecipientsModal && (
        <NotificationRecipientsModal
          csvRecipientsData={csvRecipientData}
          onClose={() => setShowRecipientsModal(false)}
          options={options}
          retailerId={retailerId}
          scheduledNotificationId={notificationId}
        />
      )}

      {showFilterModal && (
        <CustomerFilterModal
          initialFilters={Object.keys(options).length > 0 ? options : undefined}
          onApplyFilter={(filters) => {
            handleRecipientAction(filters);
            setShowFilterModal(false);
          }}
          onClose={() => setShowFilterModal(false)}
          open={true}
          retailerId={retailerId}
          testID={`${testID}-customer-filter-dialog`}
        />
      )}

      <CsvUploadModal
        onClose={() => setShowCsvUploadModal(false)}
        onUpload={onCSVUpload}
        open={showCsvUploadModal}
      />
    </Stack>
  );
};
